import React, { useEffect } from "react";
import Loader from "../components/common/loader/loader";
import { TableColumn } from "../interfaces/tables";
import Table from "../components/ui/table";
import { MdDelete, MdEdit } from "react-icons/md";
import { deleteProject, listAllProjects } from "../api/admin/projects";
import { Button } from "../components/ui/button";
import { IoKey } from "react-icons/io5";
import Error from "../components/common/error/error";
import RegenerateApiKeyDialog from "../components/projects/regenerate-api-key-dialog";
import TableHeaders from "../components/ui/table-headers";
import { siteSettings } from "../settings/site-settings";
import CreateUpdateProjectDialog from "../components/projects/create-update-project-dialog";
import DeleteProjectDialog from "../components/projects/delete-project-dialog";
import { notify } from "../lib/utils";
import usePageTitle from "../hooks/usePageTitle";

function Projects() {
  usePageTitle("Projects");
  const [projects, setProjects] = React.useState<any>([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.projects.defaultPageSize.key
  );

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  // regenerates the api key for the project
  const [selectedProjectForUpdate, setSelectedProjectForUpdate] =
    React.useState<any | null>(null);

  const [regenerateApiKeyDialogOpen, setRegenerateApiKeyDialogOpen] =
    React.useState(false);

  const [projectUpdateDialogOpen, setProjectUpdateDialogOpen] =
    React.useState(false);

  // if this true, the modal will be for edit, else for create.
  const [isProjectBeingUpdated, setIsProjectBeingUpdated] =
    React.useState(false);

  const [selectedProjectForDelete, setSelectedProjectForDelete] =
    React.useState<any | null>(null);

  const [projectDeleteDialogOpen, setProjectDeleteDialogOpen] =
    React.useState(false);

  const handleOpenDeleteProjectDialog = (category: any) => {
    setSelectedProjectForDelete(category);
    setProjectDeleteDialogOpen(true);
  };

  const handleCloseDeleteProjectDialog = () => {
    setSelectedProjectForDelete(null);
    setProjectDeleteDialogOpen(false);
  };

  const handleOpenRegenerateApiKeyDialog = (project: any) => {
    setSelectedProjectForUpdate(project);
    setRegenerateApiKeyDialogOpen(true);
  };

  const handleCloseRegenerateApiKeyDialog = () => {
    setSelectedProjectForUpdate(null);
    setRegenerateApiKeyDialogOpen(false);
  };

  const handleOpenUpdateProjectDialog = (project: any) => {
    setSelectedProjectForUpdate(project);
    setIsProjectBeingUpdated(true);
    setProjectUpdateDialogOpen(true);
  };

  const handleCloseUpdateProjectDialog = () => {
    setSelectedProjectForUpdate(null);
    setIsProjectBeingUpdated(false);
    setProjectUpdateDialogOpen(false);
  };

  const handleOnProjectUpdated = (
    updatedProject: any,
    isNewProject = false
  ) => {
    // create new project case.
    if (isNewProject) {
      fetchProjects();
    }

    // find the project in state then replace  it with the updated one
    setProjects((prevProjects: any) => {
      const index = prevProjects.findIndex(
        (project: any) => project._id === updatedProject._id
      );

      if (index === -1) return prevProjects;

      prevProjects[index] = updatedProject;

      return [...prevProjects];
    });

    setTableData(formatDataForTable(projects));
    setSelectedProjectForUpdate(updatedProject);
  };

  const fetchProjects = async () => {
    const response = await listAllProjects({
      page,
      sort,
      search,
      pageSize,
    });

    if (!response || response.status !== 200) {
      console.log("Error fetching projects");
      setError(true);
      setLoading(false);

      return;
    }

    setProjects(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
  };

  const handleProjectDelete = async (projectId: string) => {
    const response = await deleteProject(projectId);

    if (!response || response.status !== 200) {
      notify({
        type: "error",
        message: "Error deleting category",
      });
      return;
    }

    notify({
      type: "success",
      message: "Category deleted successfully",
    });

    setProjectDeleteDialogOpen(false);
    fetchProjects();
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  // headers change listener
  useEffect(() => {
    fetchProjects();
  }, [page, sort, search, pageSize]);

  const columns: TableColumn[] = [
    { key: "_id", label: "Internal Id" },
    { key: "name", label: "Name" },
    { key: "slug", label: "Slug" },

    {
      key: "createdAt",
      label: "Created At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },
    {
      key: "createdBy",
      label: "Created By (User)",
      // render: (cell) => {
      //   return (
      //     <Link className="hover:text-primary" to={`${ROUTES.USERS}/${cell}`}>
      //       {cell}
      //     </Link>
      //   );
      // },
    },

    {
      key: "apiKeyGeneratedAt",
      label: "API Key Generated At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleString() : "N/A";
      },
    },
    {
      key: "apiKeyGeneratedBy",
      label: "API Key Generated By (User)",
      // render: (cell) => {
      //   return (
      //     <Link className="hover:text-primary" to={`${ROUTES.USERS}/${cell}`}>
      //       {cell}
      //     </Link>
      //   );
      // },
    },
    {
      key: "btn",
      label: "Actions",
      fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <MdEdit
              title="Edit"
              className="hover:text-primary cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenUpdateProjectDialog(row);
              }}
            />

            <IoKey
              title="Regenerate API Key"
              className="hover:text-red-500 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenRegenerateApiKeyDialog(row);
              }}
            />

            <MdDelete
              title="Delete"
              className="hover:text-red-600 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenDeleteProjectDialog(row);
              }}
            />
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        name: d.name,
        slug: d.slug,
        createdAt: d.createdAt,
        createdBy: d.projectCreatedBy,
        apiKeyGeneratedAt: d.apiKeyGeneratedAt,
        apiKeyGeneratedBy: d.apiKeyGeneratedBy,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">Projects</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex items-end justify-end">
            <Button
              onClick={() => {
                setIsProjectBeingUpdated(false);
                setProjectUpdateDialogOpen(true);
              }}
            >
              Create Project
            </Button>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.projects.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.projects.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={siteSettings.tables.projects.pageSizeOptions}
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={siteSettings.tables.projects.defaultPageSize}
            />
            <Table columns={columns} data={tableData} />
          </div>

          <RegenerateApiKeyDialog
            project={selectedProjectForUpdate}
            open={regenerateApiKeyDialogOpen}
            onClose={handleCloseRegenerateApiKeyDialog}
            onProjectUpdated={handleOnProjectUpdated}
          />

          <CreateUpdateProjectDialog
            project={selectedProjectForUpdate}
            open={projectUpdateDialogOpen}
            onClose={handleCloseUpdateProjectDialog}
            onProjectUpdated={handleOnProjectUpdated}
            isEditMode={isProjectBeingUpdated}
          />

          <DeleteProjectDialog
            project={selectedProjectForDelete}
            open={projectDeleteDialogOpen}
            onClose={handleCloseDeleteProjectDialog}
            onProjectDelete={(categoryId: string) => {
              handleProjectDelete(categoryId);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Projects;
